/**
 * Mentorship: the thing the institution actually bought.
 *
 * ── WHAT THIS SECTION REPLACES ───────────────────────────────────────────
 * A mentored-versus-non-mentored retention curve whose own dataset label read
 * "(Pilot)", a claimed twenty-nine point retention advantage, $2.75M in hiring
 * cost avoided, a 3.8x return and "2.4x more likely to be hired within a
 * year". P3 runs no control group and holds no employment record, so all of it
 * is gone.
 *
 * ── WHAT REPLACES IT ─────────────────────────────────────────────────────
 * The strongest true finding P3 owns: students who name an institution hold a
 * mentorship at a markedly higher rate than students who do not. Verified by
 * direct query across every student record on 7 September 2026, at 78%, 68%
 * and 62% for the three institution cohorts against 39% for unaffiliated
 * students.
 *
 * The last sentence of the insight line is load-bearing and is not optional
 * copy: this is a comparison between groups, not a measure of what the
 * partnership caused. Students who seek a mentor may differ from students who
 * do not in ways nothing here measures. That sentence is what separates this
 * panel from the one it replaces.
 */

import {
  Card,
  CardBody,
  CardFootnote,
  CardHeader,
  CardTitle,
  Denominator,
  LeadFigure,
  Minor,
  ProvenanceNote,
  SectionHead,
  StateCard,
} from "~/app/components/primitives";
import { SECTION_LEDE, SECTION_TITLE } from "~/app/lib/admin-nav";
import { formatCount, formatPercent } from "~/app/lib/format";
import type { CohortView } from "~/app/lib/demo-data";
import { PairedRates, RankedList, SERIES } from "~/app/panels/panel-charts";

export function MentorshipPanel({ view }: { view: CohortView }) {
  const comparing = view.filters.compare === "platform";
  /* The comparison group is named by the view (Sep 10 2026): the sample
     compares with students who have not named an institution, a live
     tenant with every student on P3 from the same read as its own figures. */
  const { platform } = view;

  return (
    <section className="e-sec e-print-page">
      <SectionHead
        id="mentorship"
        title={SECTION_TITLE.mentorship}
        lede={SECTION_LEDE.mentorship}
      />

      <div className="e-grid">
        <Card span={2}>
          <CardHeader>
            <CardTitle sub="Counted on each student's own record.">
              Mentorship reach
            </CardTitle>
          </CardHeader>
          <CardBody>
            {view.matchedRate.kind === "value" ? (
              <div className="e-vstack">
                <Denominator
                  cell={view.matchedRate}
                  of="of your claimed students hold at least one mentorship"
                  noun="claimed students"
                  label="Mentorship reach"
                />
                {/* The bar for the cohort is always drawn. The comparator row
                    appears only when the reader asks for it, because a
                    comparison shown by default reads as a target, and P3 does
                    not set targets for an institution's students. */}
                <PairedRates
                  label={
                    comparing
                      ? `Mentorship reach at ${view.institution.legalName} against ${platform.group}`
                      : `Mentorship reach at ${view.institution.legalName}`
                  }
                  rows={[
                    {
                      name: "Your students",
                      sub: `Claimed to ${view.institution.displayName}`,
                      cell: view.matchedRate,
                      color: SERIES[0],
                    },
                    ...(comparing
                      ? [
                          {
                            name: platform.label,
                            sub: platform.note,
                            cell: platform.matched,
                            color: SERIES[1],
                          },
                        ]
                      : []),
                  ]}
                />
                {comparing ? null : (
                  <p className="e-note">
                    Choose "Compare with all P3 students" in the filters to set
                    this beside students who have not named an institution.
                  </p>
                )}
              </div>
            ) : (
              <StateCard
                variant="building"
                title="Mentorship reporting begins at ten matched students"
                progress={{ have: matchedRaw(view), need: 10, noun: "matched students" }}
              >
                Fewer than ten of your claimed students hold a mentorship, so
                this figure is not published yet.
                {platform.matched.kind === "value" ? (
                  <>
                    {" "}
                    The rate for {platform.group} is{" "}
                    {formatPercent(platform.matched.value.rate)}, which is context
                    rather than a target.
                  </>
                ) : null}
              </StateCard>
            )}
          </CardBody>
          <CardFootnote>
            <ProvenanceNote provenance={view.matchedProvenance} />
            {comparing && platform.footnote ? (
              <p className="e-note">{platform.footnote}</p>
            ) : null}
          </CardFootnote>
        </Card>

        <Card solo>
          <CardHeader>
            <CardTitle sub="The group your advising team can reach directly.">
              Awaiting mentorship
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div className="e-vstack">
              <LeadFigure
                cell={view.waitingTotal}
                format={formatCount}
                label="Awaiting mentorship"
                provenance={view.waitingProvenance}
              />
              <div className="e-minor-row" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <Minor
                  cell={view.waitingWithRequest}
                  format={formatCount}
                  label="Asked for a mentor"
                />
                <Minor
                  cell={view.waitingWithoutRequest}
                  format={formatCount}
                  label="Have not asked yet"
                />
              </div>
            </div>
          </CardBody>
          <CardFootnote>
            <p className="e-note">
              Asked for a mentor, not yet matched. No export names a student.
            </p>
            <ProvenanceNote provenance={view.waitingProvenance} />
          </CardFootnote>
        </Card>
      </div>

      <div className="e-grid">
        <Card span="full">
          <CardHeader>
            <CardTitle sub="Some students work with more than one mentor.">
              Mentorships per student
            </CardTitle>
          </CardHeader>
          <CardBody>
            {view.mentorshipCounts.allSuppressed ? (
              <StateCard variant="building" title="No group here has reached ten">
                Each of the three groups (no mentor yet, one mentor, two or more)
                is under the reporting threshold on its own, so the split is not
                published. It fills in as the cohort grows.
              </StateCard>
            ) : (
              <div className="e-vstack">
                <Minor
                  cell={view.perMatched}
                  format={(n) => n.toFixed(1)}
                  label="Mentorships per matched student"
                />
                <RankedList
                  distribution={view.mentorshipCounts}
                  label={`Students at ${view.institution.legalName} by number of mentorships held, ${view.windowLabel}`}
                  color={SERIES[2]}
                />
              </div>
            )}
          </CardBody>
          <CardFootnote>
            <p className="e-note">{view.mentorshipCounts.footnote}</p>
            <ProvenanceNote
              provenance={view.mentorshipCounts.provenance}
              suppressed={view.mentorshipCounts.hiddenCount > 0}
            />
          </CardFootnote>
        </Card>
      </div>

    </section>
  );
}

/** Only ever used to drive a threshold meter, never rendered as a figure. */
function matchedRaw(view: CohortView): number {
  return Math.max(0, view.claimedInView - view.waitingRaw);
}

